import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import Icon from '../../../components/AppIcon';

const ProgressOverview = ({ recentPlays, onPlayComplete }) => {
  const completedRef = useRef(new Set());

  useEffect(() => {
    if (!recentPlays?.length) return;

    recentPlays?.forEach((play) => {
      if (
        play?.completionPercentage >= 100 &&
        !completedRef?.current?.has(play?.id)
      ) {
        completedRef?.current?.add(play?.id);

        // Short gold confetti burst
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.5 },
          colors: ['#D4AF37', '#FFD700', '#FFF8DC'],
          startVelocity: 35,
          gravity: 1.2,
          ticks: 150,
        });

        // Notify parent to show modal
        if (onPlayComplete) {
          setTimeout(() => onPlayComplete(play?.title), 200);
        }
      }
    });
  }, [recentPlays, onPlayComplete]);

  return (
    <div className="bg-card rounded-xl shadow-md p-3 mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="TrendingUp" size={18} color="var(--color-primary)" />
          Your Progress
        </h2>
        <span className="text-xs text-muted-foreground">Last 7 days</span>
      </div>
      {recentPlays?.length === 0 ? (
        <div className="text-center py-3">
          <Icon name="BookOpen" size={28} className="text-muted-foreground mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">Start your first play to see progress here!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recentPlays?.map((play) => (
            <div key={play?.id} className="bg-muted/50 rounded-lg px-3 py-2 hover:bg-muted transition-colors duration-150">
              <div className="flex items-center justify-between mb-1">
                <div className="flex-1">
                  <h3 className="font-semibold text-card-foreground text-sm leading-tight">{play?.title}</h3>
                  <p className="text-xs text-muted-foreground">{play?.lastActivity}</p>
                </div>
                <div className="flex items-center gap-1 ml-2">
                  {play?.completionPercentage >= 100 && (
                    <span className="text-xs font-bold text-amber-400 animate-pulse">✓ Complete!</span>
                  )}
                  <span className="text-lg font-bold text-primary">{play?.completionPercentage}%</span>
                </div>
              </div>

              <div className="w-full bg-border rounded-full h-1.5 mb-1 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    play?.completionPercentage >= 100
                      ? 'bg-gradient-to-r from-amber-400 to-yellow-300' :'bg-gradient-to-r from-primary to-secondary'
                  }`}
                  style={{ width: `${Math.min(play?.completionPercentage, 100)}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="BookOpen" size={12} />
                    {play?.actsCompleted}/{play?.totalActs} Acts
                  </span>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Icon name="CheckCircle2" size={12} />
                    {play?.quizzesCompleted} Quizzes
                  </span>
                </div>
                <span className="text-accent font-medium">+{play?.xpEarned} XP</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressOverview;